import React from 'react';

import { noop } from 'ui-components/default-props/noop';

import { AddOnUpsellModal } from './add-on-upsell-modal/add-on-upsell-modal';
import { FeatureUpsellModal } from './feature-upsell-modal/feature-upsell-modal';

import { AddOnsActionContextProvider } from '../../pages/settings/add-on-marketplace/add-ons-action-context';
import { useBuildMarketplaceAddOnByCode } from '../../hooks/add-ons/use-build-addons';

/** @typedef {import('../../constants/upsell-modal-content-codes').UpsellModalContentCode} UpsellModalContentCode */

/**
 * Internal component that renders the appropriate modal based on whether the code represents an add-on or feature.
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {(addOnCode: string) => void} props.onError
 * @param {string} [props.source]
 * @param {UpsellModalContentCode} props.upsellModalContentCode
 * @returns {JSX.Element}
 */
const InternalUpsellModal = ({ upsellModalContentCode, onClose, onError, source }) => {
  const addOn = useBuildMarketplaceAddOnByCode(upsellModalContentCode);

  if (addOn) {
    return <AddOnUpsellModal addOn={addOn} onClose={onClose} onError={onError} upsellModalContentCode={upsellModalContentCode} />;
  }

  return <FeatureUpsellModal onClose={onClose} source={source} upsellModalContentCode={upsellModalContentCode} />;
};

/**
 * Facade component that renders an upsell modal for either add-ons or features.
 * Automatically detects the type based on the upsellModalContentCode and delegates
 * to the appropriate specialized modal component.
 * @param {object} props
 * @param {() => void} props.onClose
 * @param {(addOnCode: string) => void} props.onError
 * @param {string} [props.source]
 * @param {UpsellModalContentCode} props.upsellModalContentCode
 * @param {boolean} [props.withContext] default is 'false', necessary when using outside of the Settings pages
 * @returns {JSX.Element}
 */
export const UpsellModal = ({ onClose, onError = noop, source, upsellModalContentCode, withContext = false }) => {
  if (withContext) {
    return (
      <AddOnsActionContextProvider>
        <InternalUpsellModal onClose={onClose} onError={onError} source={source} upsellModalContentCode={upsellModalContentCode} />
      </AddOnsActionContextProvider>
    );
  }

  return <InternalUpsellModal onClose={onClose} onError={onError} source={source} upsellModalContentCode={upsellModalContentCode} />;
};
